import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ButtonRendererComponent } from 'src/app/renderer/button-renderer.component';
import { DashboardService } from 'src/app/_services/dashboard.service';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { TableHeaderToolTipComponent } from 'src/app/shared/table-header-tool-tip/table-header-tool-tip.component';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import moment from 'moment-timezone';
import {
  formatMaintenanceDateTime,
  formatMaintenanceDisplayDate,
  getMaintenanceDateRange,
} from '../maintenance-date-range.util';

@Component({
  selector: 'app-maintenance-job-list',
  templateUrl: './maintenance-job-list.component.html',
  styleUrl: './maintenance-job-list.component.scss',
  standalone: false,
})
export class MaintenanceJobListComponent
  extends SubscriptionUtil
  implements OnInit, OnDestroy
{
  public filterActive = true;
  title: any;
  data: any;

  totalRecord: any;
  searchValue: any;
  searchValueData = false;
  translate: any;

  gridApi: any;
  gridOptions: any;
  columnDefs: any;
  defaultColDef: any;
  overlayNoRowsTemplate: any;
  frameworkComponents: any;
  pageSize: any = 25;
  gridParams: any;
  startFrom: any;
  parameters: any;
  maintenanceJobs: any[] = [];

  timeRangeOptions = [
    { label: 'Last Year', value: 'LAST_YEAR' },
    { label: 'Last Quarter', value: 'LAST_QUARTER' },
    { label: 'Last Month', value: 'LAST_MONTH' },
    { label: 'Last 7 Days', value: 'LAST_7_DAYS' },
    { label: 'Next 7 Days', value: 'NEXT_7_DAYS' },
    { label: 'Next Month', value: 'NEXT_MONTH' },
    { label: 'Next Quarter', value: 'NEXT_QUARTER' },
    { label: 'Next Year', value: 'NEXT_YEAR' },
    { label: 'Custom', value: 'CUSTOM' },
  ];
  equipmentTypeOptions: any[] = [];
  groupOptions: any[] = [];
  serviceZoneOptions: any[] = [];
  equipmentStatusOptions: any[] = [];

  filteredOptions = {
    dateFilter: 'LAST_7_DAYS',
    assetTypeIds: null as string | number | null,
    assetServiceZoneIds: null as string | number | null,
    jobOrderServiceZoneIds: null as string | number | null,
    userGroupIds: null as string | number | null,
    startDateTime: null as Date | null,
    endDateTime: null as Date | null,
  };
  customDateRange: Date[] | null = null;

  constructor(
    public modalRef: BsModalRef,
    private dashboardService: DashboardService,
    private errorUtil: ErrorUtil,
    private languageTranslateService: LanguageTranslateService,
    private router: Router,
    private util: UtilServiceService,
    private auth: AuthenticationService,
  ) {
    super();
    this.preInit();
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.onDestroy();
  }

  /******************************************** Pre Init ******************************************/
  private preInit() {
    this.frameworkComponents = {
      buttonRenderer: ButtonRendererComponent,
    };

    this.gridOptions = {
      cacheBlockSize: 25,
      paginationPageSize: 25,
      rowModelType: 'infinite',
      sortingOrder: ['desc', 'asc'],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      sortable: true,
      resizable: true,
      filter: false,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      tooltipComponent: TableHeaderToolTipComponent,
    };

    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; font-size: 14px;">Record not found</span>';
  }

  /******************************************** Init ******************************************/
  private init() {
    this.initFilterData();
    this.getLanguageData();
  }

  private initFilterData(): void {
    const filters = this.data?.filters || {};
    this.equipmentTypeOptions = this.data?.equipmentTypeOptions || [];
    this.groupOptions = this.data?.groupOptions || [];
    this.serviceZoneOptions = this.data?.serviceZoneOptions || [];
    this.equipmentStatusOptions = this.data?.equipmentStatusOptions || [];

    this.filteredOptions = {
      dateFilter: filters.dateFilter || 'LAST_7_DAYS',
      assetTypeIds: filters.assetTypeIds ?? null,
      assetServiceZoneIds: filters.assetServiceZoneIds ?? null,
      jobOrderServiceZoneIds: filters.jobOrderServiceZoneIds ?? null,
      userGroupIds: filters.userGroupIds ?? null,
      startDateTime: filters.startDateTime
        ? new Date(filters.startDateTime)
        : null,
      endDateTime: filters.endDateTime ? new Date(filters.endDateTime) : null,
    };

    if (
      this.filteredOptions.dateFilter === 'CUSTOM' &&
      this.filteredOptions.startDateTime &&
      this.filteredOptions.endDateTime
    ) {
      this.customDateRange = [
        this.filteredOptions.startDateTime,
        this.filteredOptions.endDateTime,
      ];
    } else {
      this.customDateRange = null;
      this.syncDateRangeFromFilter();
    }
  }

  private syncDateRangeFromFilter(): void {
    const range = getMaintenanceDateRange(
      this.filteredOptions.dateFilter,
      this.filteredOptions.startDateTime,
      this.filteredOptions.endDateTime
    );
    if (!range || this.filteredOptions.dateFilter === 'CUSTOM') {
      return;
    }
    this.filteredOptions.startDateTime = range.start;
    this.filteredOptions.endDateTime = range.end;
  }

  /******************************************** On Destroy ******************************************/
  private onDestroy() {
    super.ngOnDestroy();
  }

  /******************************************** Language ******************************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length !== 0) {
          this.translate = data;
          this.initializeColumns();
        }
      })
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  /******************************************** On Grid Ready ******************************************/
  public onGridReady(params: any) {
    this.gridParams = params;
    this.searchValue = this.searchValue ? this.searchValue : '';
    this.gridApi = params.api;

    const dataSource = {
      getRows: (params: any) => {
        this.startFrom = params.startRow + 1;

        if (params.sortModel.length <= 0) {
          this.parameters = { colId: 'installationTime', sort: 'desc' };
          params.sortModel.push(this.parameters);
        }

        const filters = this.filteredOptions;
        if (!filters?.dateFilter) {
          this.totalRecord = 0;
          params.successCallback([], 0);
          this.gridApi.showNoRowsOverlay();
          return;
        }

        if (
          filters.dateFilter === 'CUSTOM' &&
          (!filters.startDateTime || !filters.endDateTime)
        ) {
          this.totalRecord = 0;
          params.successCallback([], 0);
          this.gridApi.showNoRowsOverlay();
          return;
        }

        const apiParams = this.buildApiParams(filters, params);

        this.push(
          this.dashboardService.getMaintenances(apiParams).subscribe(
            (res: any) => {
              this.gridApi.hideOverlay();

              const list = this.extractList(res);
              this.maintenanceJobs = list.map((item: any) => this.mapRow(item));
              const total = this.extractTotal(res, list);
              const body = res?.body ?? res;
              const status =
                body?.status ??
                res?.status ??
                (list.length ? 'OK' : 'NO_DATA');

              if (
                status === 'NO_DATA' ||
                status === 'SEARCH_NO_RESULTS' ||
                this.maintenanceJobs.length === 0
              ) {
                this.gridApi.showNoRowsOverlay();
                this.totalRecord = 0;
                params.successCallback([], 0);
              } else {
                this.totalRecord = total;
                params.successCallback(this.maintenanceJobs, total);
              }
            },
            (error) => {
               var errorTitle = error.error.title
              if (error.error.type == 'dynamic-lang-error') {
                var data = error.error.instance + ".validationBE." + error.error.detail;
                var message = this.languageTranslateService.getNestedValue(this.translate, data);
                errorTitle = message
              }
              this.errorUtil.setErrorMessage(
                error.status,
                null,
                errorTitle,
                "error",
                2000
              );
              this.totalRecord = 0;
              params.successCallback([], 0);
              this.gridApi.showNoRowsOverlay();
            }
          )
        );
      },
    };

    this.gridApi.sizeColumnsToFit();
    this.gridApi.setGridOption('datasource', dataSource);
  }

  /******************************************** Build API Params ******************************************/
  private buildApiParams(filters: any, gridParams: any): any {
    const apiParams: any = {
      dateFilter: filters.dateFilter,
      assetTypeIds: this.resolveFilterId(filters.assetTypeIds),
      userGroupIds: this.resolveFilterId(filters.userGroupIds),
      jobOrderServiceZoneIds: this.resolveFilterId(filters.jobOrderServiceZoneIds),
      assetServiceZoneIds: this.resolveFilterId(filters.assetServiceZoneIds),
      columnName: gridParams.sortModel[0].colId || 'installationTime',
      sortOrder: gridParams.sortModel[0].sort || 'asc',
      perPage: this.pageSize,
      startFrom: this.startFrom,
      search: this.searchValue || null,
    };

    if (filters.dateFilter === 'CUSTOM') {
      const range = getMaintenanceDateRange(
        filters.dateFilter,
        filters.startDateTime,
        filters.endDateTime
      );
      if (range) {
        apiParams.startDateTime = formatMaintenanceDateTime(range.start);
        apiParams.endDateTime = formatMaintenanceDateTime(range.end, true);
      }
    }

    return apiParams;
  }

  private resolveFilterId(value: any): string | number | null {
    if (value == null || value === '') {
      return null;
    }
    if (typeof value === 'object') {
      return value.id ?? value.value ?? null;
    }
    return value;
  }

  private extractList(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (Array.isArray(res?.body?.data)) {
      return res.body.data;
    }
    if (Array.isArray(res?.body)) {
      return res.body;
    }
    return [];
  }

  private extractTotal(res: any, list: any[]): number {
    return (
      res?.body?.total ??
      res?.total ??
      res?.body?.totalElements ??
      res?.totalElements ??
      list.length
    );
  }

  private mapRow(item: any): any {
    const asset = item?.asset || {};
    const assetStatus = asset?.assetStatus || asset?.assetsStatus || null;
    const matchedStatus = this.getMatchedEquipmentStatus(assetStatus);

    return {
      ...item,
      jobOrderId: item?.jobOrder?.id || item?.jobOrderId || item?.id,
      jobOrderNo:
        item?.jobOrderNo ||
        item?.jobOrderNumber ||
        item?.joNumber ||
        item?.number ||
        item?.jobOrder?.jobOrderNo ||
        '-',
      equipmentName: asset?.name || item?.equipmentName || item?.assetName || '-',
      equipmentId:
        item?.equipmentId ||
        item?.assetId ||
        asset?.id ||
        item?.equipment?.id ||
        item?.asset?.id ||
        null,
      equipmentStatus: matchedStatus?.name || assetStatus?.name || '-',
      equipmentStatusClass:
        matchedStatus?.colorCode || assetStatus?.colorCode || '',
      maintenancePlan:
        item?.maintenancePlan?.name ||
        item?.planName ||
        item?.maintenancePlanName ||
        '-',
      installationTime: item?.installationTime || item?.jobOrder?.installationTime || null,
      orderDate: this.formatOrderDate(item?.installationTime || item?.jobOrder?.installationTime),
      assignee:
        item?.orderOwner?.fullName ||
        item?.assignee?.fullName ||
        'Unassigned',
      maintenanceStatus: item?.maintenanceStatus,
      endDateTime: item?.endDateTime,
      overdue: this.getOverdueLabel(item),
    };
  }

  private formatOrderDate(installationTime: any): string {
    if (installationTime == null || installationTime === '') {
      return '-';
    }

    const dateValue =
      typeof installationTime === 'object'
        ? installationTime.date ||
          installationTime.dateTime ||
          installationTime.value ||
          installationTime
        : installationTime;

    const timezone = this.auth.getUserTimezone();
    const dateFormat = localStorage.getItem('date_format')?.toUpperCase() || 'DD/MM/YYYY';
    const parsed = moment.tz(dateValue, timezone);

    if (!parsed.isValid()) {
      return '-';
    }

    return `${parsed.format(dateFormat)}`;
  }

  private getMatchedEquipmentStatus(assetStatus: any): any {
    const options = this.equipmentStatusOptions || [];
    if (!assetStatus?.id || !options?.length) {
      return null;
    }
    return (
      options.find(
        (status: any) => String(status.id) === String(assetStatus.id)
      ) || null
    );
  }

  private getOverdueLabel(item: any): string {
    const maintenanceStatus = String(
      item?.maintenanceStatus ||
      item?.status?.name ||
      item?.status?.code ||
      item?.status ||
      ''
    ).toUpperCase();

    const endDateTime = item?.endDateTime

    if (!endDateTime) {
      const overdueDays = item?.overdueDays ?? item?.daysOverdue;
      if (overdueDays != null && overdueDays !== '') {
        const days = Number(overdueDays);
        if (!Number.isNaN(days) && days > 0) {
          return `${days} day${days === 1 ? '' : 's'} overdue`;
        }
      }
      return '-';
    }

    const endDate = new Date(endDateTime);
    if (Number.isNaN(endDate.getTime())) {
      return '-';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    const diffDays = Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const days = Math.abs(diffDays);

    if (maintenanceStatus === 'OVERDUE' && diffDays < 0) {
      return `${days} day${days === 1 ? '' : 's'} overdue`;
    }

    if (diffDays === 0) {
      return 'Due today';
    }
    if(maintenanceStatus === 'COMPLETED'){
      return 'Completed';
    }

    return `Overdue in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  }

  /******************************************** Search ******************************************/
  public onSearch() {
    if (this.searchValue && this.searchValue.length > 0) {
      this.searchValueData = true;
      this.refresh();
      return;
    }

   
  }
public onSearchCheck() {
    if (this.searchValue?.length == 0) {
      this.searchValueData = false;
      this.searchValue = '';
      this.refresh();
    }
  }

  public onClear() {
    this.searchValueData = false;
    this.searchValue = '';
    this.refresh();
  }

  /******************************************** Acts as Refresh ******************************************/
  

  public refresh() {
     this.onGridReady(this.gridParams);
  }

  /******************************************** View ******************************************/
  public view(e: any) {
    if (e.colDef.field === 'jobOrderNo' && e.data?.jobOrderId) {
      this.openJob(e.data.jobOrderId);
      return;
    }

    if (e.colDef.field === 'equipmentName' && e.data?.equipmentId) {
      this.openAsset(e.data.equipmentId);
    }
  }

  private openJob(jobOrderId: string | number) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/jobOrders/view/' + jobOrderId], {
        queryParams: { jobOrderId },
      })
    );
    this.util.openItemToNewTab(url);
  }

  private openAsset(equipmentId: string | number) {
    localStorage.setItem('activeIndexAssetView', '0');
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/assets/view', equipmentId], {
        queryParams: { id: equipmentId },
      })
    );
    this.util.openItemToNewTab(url);
  }

  /******************************************** Context Menu ******************************************/
  public getContextMenuItems = (params: any) => {
    params.node.data.field = params.column.colId;
    const results: any[] = [];

    if (params.column.colId === 'jobOrderNo' && params.node.data?.jobOrderId) {
      results.push({
        name: 'Open link in new tab',
        action: () => this.openJob(params.node.data.jobOrderId),
      });
    }

    if (params.column.colId === 'equipmentName' && params.node.data?.equipmentId) {
      results.push({
        name: 'Open link in new tab',
        action: () => this.openAsset(params.node.data.equipmentId),
      });
    }

    return results;
  };

  /******************************************** toggle filter ******************************************/
  public toggleFilter() {
      this.filterActive = !this.filterActive
  }

  get displayDateRange(): string {
    const range = getMaintenanceDateRange(
      this.filteredOptions.dateFilter,
      this.filteredOptions.startDateTime,
      this.filteredOptions.endDateTime
    );

    if (!range) {
      return '';
    }

    return `${formatMaintenanceDisplayDate(range.start)} - ${formatMaintenanceDisplayDate(range.end)}`;
  }

  get isCustomDateRangeRequired(): boolean {
    return (
      this.filteredOptions.dateFilter === 'CUSTOM' &&
      (!this.filteredOptions.startDateTime || !this.filteredOptions.endDateTime)
    );
  }

  onTimeRangeChange(value: string): void {
    this.filteredOptions.dateFilter = value;
    if (value !== 'CUSTOM') {
      this.customDateRange = null;
      this.syncDateRangeFromFilter();
      this.refresh();
    } else {
      this.filteredOptions.startDateTime = null;
      this.filteredOptions.endDateTime = null;
    }
  }

  onCustomDateRangeChange(range: Date[] | null): void {
    this.customDateRange = range;
    if (range?.[0] && range?.[1]) {
      this.filteredOptions.startDateTime = range[0];
      this.filteredOptions.endDateTime = range[1];
      this.refresh();
    } else if (range?.[0]) {
      this.filteredOptions.startDateTime = range[0];
      this.filteredOptions.endDateTime = null;
    } else {
      this.filteredOptions.startDateTime = null;
      this.filteredOptions.endDateTime = null;
    }
  }

  onFilterChange(
    key: 'assetTypeIds' | 'userGroupIds' | 'assetServiceZoneIds',
    value: any
  ): void {
    this.filteredOptions[key] = value;
    this.refresh();
  }

  clearAll(): void {
    this.filteredOptions = {
      dateFilter: 'LAST_7_DAYS',
      assetTypeIds: null,
      assetServiceZoneIds: null,
      jobOrderServiceZoneIds: null,
      userGroupIds: null,
      startDateTime: null,
      endDateTime: null,
    };
    this.customDateRange = null;
    this.syncDateRangeFromFilter();
    this.refresh();
  }

  /******************************************** Columns ******************************************/
  private initializeColumns() {
    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; font-size: 14px;">' +
      (this.translate?.general?.messages?.noRecordsFound || 'Record not found') +
      '</span>';

    this.columnDefs = [
      {
        headerName: this.translate.jobOrder.label.singular,
        field: 'jobOrderNo',
        width: 140,
        headerTooltip: '#',
        cellClass: function () {
          return ['text-1-5 font-weight-bold table_default_color'];
        },
      },
      {
        headerName: this.translate?.asset.label.singular,
        field: 'equipmentName',
        width: 180,
        headerTooltip: '#',
        cellClass: (params: any) => {
          if (params?.data?.equipmentId) {
            return ['text-1-5 font-weight-bold table_default_color'];
          }
          return [];
        },
      },
      {
        headerName: this.translate?.asset.label.singular + ' ' + this.translate?.asset.fields.status.singular,
        field: 'equipmentStatus',
        width: 150,
        headerTooltip: '#',
        cellRenderer: function (params: any) {
          if (!params?.data) {
            return '';
          }
          const status = params.data.equipmentStatus || '-';
          const color = params.data.equipmentStatusClass;
          if (color) {
            return (
              '<span class="p-mr-2 p-tag p-component" style="background-color:' +
              color +
              '50 !important ;color:' +
              color +
              '"><span class="p-tag-value text-dark">' +
              status +
              '</span></span>'
            );
          }
          return status;
        },
      },
      {
        headerName: this.translate?.maintenancePlan.sections.sec1.singular,
        field: 'maintenancePlan',
        width: 180,
        headerTooltip: '#',
      },   
      {
        headerName: 'Assignee',
        field: 'assignee',
        width: 150,
        headerTooltip: '#',
      },
      {
        headerName: this.translate?.jobOrder?.fields?.orderDate?.singular || 'Order Date',
        field: 'orderDate',
        colId: 'installationTime',
        width: 170,
        headerTooltip: '#',
      },
      {
        headerName: this.translate?.general.status.overdue.singular,
        field: 'overdue',
        width: 150,
        headerTooltip: '#',
        cellRenderer: function (params: any) {
          if (!params?.data) {
            return '';
          }

          // Match dashboard preview logic:
          // !endDateTime -> "-"
          // OVERDUE + endDateTime -> overdue style
          // not OVERDUE + endDateTime -> normal style
          if (!params.data.endDateTime) {
            return '<span class="maintenance-jobs__cell-value">-</span>';
          }

          const label = params.data.overdue || '-';
          if (params.data.maintenanceStatus === 'OVERDUE') {
            return (
              '<span class="maintenance-jobs__cell-value maintenance-jobs__cell-value--overdue">' +
              label +
              '</span>'
            );
          }

          return '<span class="maintenance-jobs__cell-value">' + label + '</span>';
        },
      },
    ];

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 300);
    }
  }
}
